// Changes to approve (ADR 0041): what one platform administrator asked for
// and another must approve before it takes effect. Nobody approves their own
// request; each approval is confirmed with a code.
import { Badge, Button, Group, Loader, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useMe } from "../../shared/session/me";
import { useStepUp } from "../../shared/session/stepup";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";
import { APPROVALS_KEY, type Approval } from "./approvals";

const STATUS_COLOR: Record<Approval["status"], string> = {
  pending: "orange",
  applying: "blue",
  approved: "green",
  rejected: "gray",
  failed: "red",
  expired: "gray",
};

export function AdminApprovalsPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")} intro={t("changes.intro")}>
      <Approvals />
    </AdminLayout>
  );
}

function Approvals() {
  const { t } = useTranslation();
  const list = useQuery({ queryKey: APPROVALS_KEY, queryFn: () => unwrap(api.GET("/admin/approvals")) });
  const team = useQuery({ queryKey: ["admin", "admins"], queryFn: () => unwrap(api.GET("/admin/admins")) });
  const names = new Map((team.data?.items ?? []).map((a) => [a.account_id, a.masked_email]));
  const items = list.data?.items ?? [];
  const waiting = items.filter((a) => a.status === "pending");
  const done = items.filter((a) => a.status !== "pending");

  if (list.isPending) return <Loader size="sm" aria-label={t("common.loading")} />;
  if (list.isError) return <FormError message={errorMessage(list.error, t("error.generic"))} />;
  return (
    <Stack gap="lg">
      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <Title order={2} size="h4">
            {t("changes.waiting_title")}
          </Title>
          {waiting.length === 0 ? (
            <Text size="sm" c="dimmed">
              {t("changes.none_waiting")}
            </Text>
          ) : (
            waiting.map((a) => <ApprovalCard key={a.id} approval={a} names={names} />)
          )}
        </Stack>
      </Paper>
      {done.length > 0 && (
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Title order={2} size="h4">
              {t("changes.history_title")}
            </Title>
            <History items={done} names={names} />
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

function Summary({ approval: a }: { approval: Approval }) {
  const { t } = useTranslation();
  return (
    <Table withRowBorders={false} verticalSpacing={2} aria-label={t("changes.what")}>
      <Table.Tbody>
        {a.summary.map((c) => (
          <Table.Tr key={c.field}>
            <Table.Td>
              <Text size="sm" fw={600}>
                {t(`changes.field.${c.field}`, { defaultValue: c.field })}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{c.from ? `${c.from} → ${c.to}` : c.to}</Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function ApprovalCard({ approval: a, names }: { approval: Approval; names: Map<string, string> }) {
  const { t } = useTranslation();
  const f = useFormat();
  const me = useMe();
  const stepUp = useStepUp();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Approval | null>(null);
  const mine = a.requested_by === me.data?.account_id;

  const decide = async (decision: "approve" | "reject") => {
    setError(null);
    let token: string | null = null;
    if (decision === "approve") {
      token = await stepUp("admin_change");
      if (!token) return;
    }
    setBusy(decision);
    const { data, error } = await api.POST("/admin/approvals/{approval_id}/decision", {
      params: { path: { approval_id: a.id }, header: token ? { "X-Step-Up-Token": token } : {} },
      body: { decision },
    });
    setBusy(null);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setResult(data);
    void queryClient.invalidateQueries({ queryKey: ["admin"] });
  };

  return (
    <Paper withBorder p="sm" radius="md" data-testid="approval">
      <Stack gap="xs">
        <Group justify="space-between" wrap="wrap">
          <Text fw={600}>{t(`changes.action.${a.action}`)}</Text>
          <Text size="xs" c="dimmed">
            {t("changes.asked_by", {
              who: mine ? t("changes.you") : (names.get(a.requested_by) ?? t("changes.another_admin")),
              date: f.dateTime(a.created_at),
            })}
          </Text>
        </Group>
        <Summary approval={a} />
        <Text size="xs" c="dimmed">
          {t("changes.expires", { date: f.dateTime(a.expires_at) })}
        </Text>
        {result ? (
          <Notice>
            {result.status === "approved"
              ? t("changes.done_approved")
              : result.status === "failed"
                ? t("changes.done_failed", { reason: result.result })
                : t("changes.done_rejected")}
          </Notice>
        ) : (
          <Group gap="xs">
            {mine ? (
              <Text size="sm" c="dimmed">
                {t("changes.yours")}
              </Text>
            ) : (
              <Button
                size="xs"
                loading={busy === "approve"}
                disabled={busy !== null}
                onClick={() => void decide("approve")}
              >
                {t("changes.approve")}
              </Button>
            )}
            <Button
              size="xs"
              variant="light"
              color="red"
              loading={busy === "reject"}
              disabled={busy !== null}
              onClick={() => void decide("reject")}
            >
              {mine ? t("changes.withdraw") : t("changes.reject")}
            </Button>
          </Group>
        )}
        <FormError message={error} />
      </Stack>
    </Paper>
  );
}

function History({ items, names }: { items: Approval[]; names: Map<string, string> }) {
  const { t } = useTranslation();
  const f = useFormat();
  const who = (id: string | undefined) => (id ? (names.get(id) ?? t("changes.another_admin")) : "");
  return (
    <Table.ScrollContainer minWidth={640}>
      <Table verticalSpacing="sm" aria-label={t("changes.history_title")}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("changes.when")}</Table.Th>
            <Table.Th>{t("changes.what")}</Table.Th>
            <Table.Th>{t("changes.asked")}</Table.Th>
            <Table.Th>{t("changes.decided")}</Table.Th>
            <Table.Th>{t("changes.status")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((a) => (
            <Table.Tr key={a.id} data-testid="approval-history">
              <Table.Td>{f.dateTime(a.created_at)}</Table.Td>
              <Table.Td>
                <Text size="sm">{t(`changes.action.${a.action}`)}</Text>
                {a.result && (
                  <Text size="xs" c="red.8">
                    {a.result}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>{who(a.requested_by)}</Table.Td>
              <Table.Td>{who(a.decided_by)}</Table.Td>
              <Table.Td>
                <Badge color={STATUS_COLOR[a.status]} variant="light">
                  {t(`changes.state.${a.status}`)}
                </Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
